import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

type InputSize = 'sm' | 'md';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helpText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  inputSize?: InputSize;
}

const sizeClasses: Record<InputSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3 py-2 text-sm',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helpText, leftIcon, rightIcon, inputSize = 'md', id, className = '', ...props }, ref) => {
    const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    return (
      <div className={className}>
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-surface-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-surface-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={[
              'block w-full rounded-lg border bg-white transition-colors',
              'focus:outline-none focus:ring-2 focus:border-transparent',
              error
                ? 'border-danger-500 focus:ring-danger-500'
                : 'border-surface-300 focus:ring-primary-500',
              sizeClasses[inputSize],
              leftIcon ? 'pl-10' : '',
              rightIcon ? 'pr-10' : '',
              'disabled:bg-surface-50 disabled:text-surface-500 disabled:cursor-not-allowed',
            ].join(' ')}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-surface-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-danger-600">{error}</p>}
        {helpText && !error && <p className="mt-1 text-sm text-surface-500">{helpText}</p>}
      </div>
    );
  },
);

Input.displayName = 'Input';
